import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { DesktopFooter } from './DesktopFooter';
import { CookieConsent } from '../CookieConsent';
import { MobileNav } from './MobileNav';
import { MobileBottomNav } from './MobileBottomNav';
import { FloatingActionButton } from './FloatingActionButton';
import { AnnouncementBanner } from './AnnouncementBanner';
import { useSwipeToOpenMenu } from '../../hooks/useSwipeToOpenMenu';
import { Schema } from '../Schema';

interface MainLayoutProps {
  children: React.ReactNode;
  onLogin: () => void;
  isAuthenticated: boolean;
}

function MainLayout({ children, onLogin, isAuthenticated }: MainLayoutProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileNav] = useState(true);
  const [shouldRenderMobileNav, setShouldRenderMobileNav] = useState(false);

  // Hide footer on chat pages
  const isChatPage = location.pathname.startsWith('/chat');

  useEffect(() => {
    if (isMobileMenuOpen) {
      setShouldRenderMobileNav(true);
    } else {
      const timeout = setTimeout(() => setShouldRenderMobileNav(false), 0);
      return () => clearTimeout(timeout);
    }
  }, [isMobileMenuOpen]);

  // Enable swipe gestures to open/close mobile menu
  useSwipeToOpenMenu({
    onOpen: () => setIsMobileMenuOpen(true),
    onClose: () => setIsMobileMenuOpen(false),
    isOpen: isMobileMenuOpen,
    enabled: true,
  });

  return (
    <div className="min-h-screen min-h-[100dvh] bg-white">
      <Schema
        type="Organization"
        data={{
          name: 'Mindful Family',
          url: 'https://mindful.family',
          description: 'A global platform where wellness communities, holistic health practitioners, and eco-conscious enthusiasts unite to promote mindful living.',
          contactPoint: {
            email: 'support@mindful.family',
            contactType: 'Customer Service',
          },
          sameAs: [
            'https://www.facebook.com/profile.php?id=61562249256779',
            'https://www.instagram.com/mindful.family_official/',
          ],
        }}
      />
      <CookieConsent onAccept={() => {}} />
      
      {/* Mobile Menu */}
      {shouldRenderMobileNav && (
        <div className="lg:hidden">
          <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </div>
      )}

      <div
        className="flex flex-col min-h-full min-h-[100dvh]"
      >
        <Header
          onLogin={onLogin}
          isAuthenticated={isAuthenticated}
          onMenuClick={() => setIsMobileMenuOpen(true)}
        />
        {!isChatPage && <AnnouncementBanner isAuthenticated={isAuthenticated} />}
        <main className={`flex-1 relative pb-20 lg:pb-0 ${isChatPage ? 'overflow-hidden flex flex-col' : ''}`}>
          {isChatPage ? (
            /* Chat pages: flex layout for height filling */
            <div className="flex-1 min-h-0 flex flex-col">
              {children}
            </div>
          ) : (
            /* Normal pages: natural flow with footer on desktop */
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 min-h-0 pb-10">
                {children}
              </div>
              <DesktopFooter />
            </div>
          )}
        </main>
        <FloatingActionButton />
      </div>
      <MobileBottomNav />
    </div>
  );
}

export default MainLayout;